import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AlertTriangle, MapPin } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { kitSourceChosen } from '../../../../features/operatorKits/operatorKitsSlice';
import {
  KIT_WORK,
  disassemblySource,
  givesText,
  kitName,
  kitPlanShortText,
  planLegs,
  sourceKeyOf,
  sourceOption,
  sourceText,
} from '../../../../features/operatorKits/operatorKitsUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const Source = ({ productId, sku, leg }) => (
  <li
    data-testid="kit-source"
    data-product-id={productId}
    data-slot-code={leg.slotCode}
    data-lot={leg.lot}
    data-quantity={leg.quantity}
    className="flex items-start gap-2 text-base font-semibold text-ninja-dark dark:text-white"
  >
    <MapPin size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-ninja-green" />
    <span>
      {sourceText(sku, leg)}
      {leg.expiryDate && <span className="block text-sm font-normal text-text-secondary">Vence {leg.expiryDate}</span>}
    </span>
  </li>
);

const ChangeButton = ({ productId, locked, onChange }) => (
  <Button
    variant="secondary"
    size="lg"
    disabled={locked}
    onClick={() => onChange(productId)}
    data-testid="kit-source-change"
    data-product-id={productId}
  >
    Cambiar
  </Button>
);

/**
 * Where it all comes from, as the plan says it and as the operator changed it:
 * putting kits together, each component from its slots; taking them apart,
 * the kits out of one slot, and what they give. «Cambiar» puts another place
 * first. Nothing is scanned here: the operator gathers it and says so.
 *
 * @param {object} props
 * @param {object} props.visit - The `operatorKits` slice.
 * @param {boolean} props.locked - A write is out or unconfirmed.
 */
export const KitSources = ({ visit, locked }) => {
  const dispatch = useDispatch();
  const [changing, setChanging] = useState(null);
  const { plan, preferred, mode, quantity, kit } = visit;
  const sku = kitName(kit);
  const shortText = kitPlanShortText(plan, preferred, mode, quantity);

  let options = [];
  let suggested = null;
  let body;
  if (mode === KIT_WORK.DISASSEMBLE) {
    const source = disassemblySource(plan, preferred);
    options = plan.options || [];
    suggested = plan.source ? sourceKeyOf(plan.source) : null;
    body = (
      <>
        <p className="text-base font-bold text-ninja-dark dark:text-white">Saca {formatQuantity(quantity)} {sku}</p>
        <div className="space-y-3 rounded-2xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
          <div className="flex items-start justify-between gap-3">
            <ul className="min-w-0 space-y-2">
              {source && <Source productId={kit.id} sku={sku} leg={{ ...source, quantity: Number(quantity) }} />}
            </ul>
            {options.length > 1 && <ChangeButton productId={kit.id} locked={locked} onChange={setChanging} />}
          </div>
          <p className="text-sm text-text-secondary">Salen: {givesText(plan)}</p>
        </div>
      </>
    );
  } else {
    const components = planLegs(plan, preferred);
    const changed = components.find((component) => component.productId === changing);
    options = changed?.options || [];
    const planned = (plan.components || []).find((component) => component.product_id === changing);
    suggested = planned?.legs?.[0] ? sourceKeyOf(planned.legs[0]) : null;
    body = (
      <>
        <p className="text-base font-bold text-ninja-dark dark:text-white">
          Junta los componentes de {formatQuantity(quantity)} {sku}
        </p>
        <ul className="space-y-3">
          {components.map((component) => (
            <li
              key={component.productId}
              className="space-y-2 rounded-2xl border border-border bg-white p-4 dark:bg-[#1a1a1a]"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-text-secondary">
                  {formatQuantity(component.needed)} {component.sku}
                </p>
                {component.options.length > 1 && (
                  <ChangeButton productId={component.productId} locked={locked} onChange={setChanging} />
                )}
              </div>
              <ul className="space-y-2">
                {component.legs.map((leg) => (
                  <Source key={`${leg.slotCode}|${leg.lot}`} productId={component.productId} sku={component.sku} leg={leg} />
                ))}
              </ul>
              {component.short > 0 && (
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                  {component.short === 1 ? 'Falta' : 'Faltan'} {formatQuantity(component.short)} de {component.sku}
                </p>
              )}
            </li>
          ))}
        </ul>
      </>
    );
  }

  const sheetProduct = mode === KIT_WORK.DISASSEMBLE ? sku : (plan.components || []).find((c) => c.product_id === changing)?.sku;

  return (
    <section className="space-y-3" data-testid="kit-plan" data-mode={mode}>
      {body}
      {shortText && (
        <p
          data-testid="kit-plan-short"
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-[#2a2114] dark:text-amber-300"
        >
          <AlertTriangle size={20} aria-hidden="true" className="shrink-0" />
          {shortText}
        </p>
      )}
      <CandidatesSheet
        isOpen={changing != null}
        title="¿De dónde lo sacas?"
        message={sheetProduct ? `${sheetProduct}: elige de qué ubicación sacas primero.` : undefined}
        options={options.map((option) => sourceOption(option, suggested))}
        onChoose={(key) => {
          dispatch(kitSourceChosen({ productId: changing, key }));
          setChanging(null);
        }}
        onClose={() => setChanging(null)}
      />
    </section>
  );
};
